import type {CanvasFooterAdvisor} from './canvas-footer';
import type {CanvasBrandColors, CanvasData} from './types';

const LIGHT_ON_PRIMARY = '#ffffff';
const DARK_ON_PRIMARY = '#1c1917';

export const DEFAULT_CANVAS_BRAND = {
  primary: '#1e3a5f',
  accent: '#b4d2f0',
  fontStyle: 'SANS_SERIF',
  cornerStyle: 'ROUNDED',
} as const satisfies Required<CanvasBrandColors>;

type CanvasBrandInput = {
  [Key in keyof CanvasBrandColors]?: CanvasBrandColors[Key] | null;
};

// Parses #abc and #aabbcc into 0-255 channels; null for anything else.
function hexToRgb(hex: string): [number, number, number] | null {
  if (!hex.startsWith('#') || (hex.length !== 4 && hex.length !== 7)) {
    return null;
  }
  const full =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
  return [
    parseInt(full.slice(1, 3), 16),
    parseInt(full.slice(3, 5), 16),
    parseInt(full.slice(5, 7), 16),
  ];
}

// Converts a hex color to rgba with a given alpha, passing through non-hex
// values unchanged.
function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return hex;
  }
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Picks white or near-black for text sitting on `background`, whichever has the
// better WCAG contrast ratio. Brand primaries are arbitrary per-organization
// hexes, so a light one would otherwise get unreadable white text.
export function foregroundFor(background: string): string {
  const rgb = hexToRgb(background);
  if (!rgb) {
    return LIGHT_ON_PRIMARY;
  }

  const [r, g, b] = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithDark = (luminance + 0.05) / 0.05;

  return contrastWithWhite >= contrastWithDark
    ? LIGHT_ON_PRIMARY
    : DARK_ON_PRIMARY;
}

/**
 * Materializes the default Ocean brand when an organization has not saved a
 * brand profile yet. Individual null fields also fall back so every renderer
 * receives a complete, usable palette.
 */
export function resolveCanvasBrand(
  brand?: CanvasBrandInput | null,
): Required<CanvasBrandColors> {
  return {
    primary: brand?.primary ?? DEFAULT_CANVAS_BRAND.primary,
    accent: brand?.accent ?? DEFAULT_CANVAS_BRAND.accent,
    fontStyle: brand?.fontStyle ?? DEFAULT_CANVAS_BRAND.fontStyle,
    cornerStyle: brand?.cornerStyle ?? DEFAULT_CANVAS_BRAND.cornerStyle,
  };
}

/**
 * Builds canvas CSS variable overrides from optional brand colors.
 * Always returns defaults so the vars are never undefined.
 */
export function buildCanvasCssVars(
  brand?: CanvasBrandColors,
): Record<string, string> {
  const resolvedBrand = resolveCanvasBrand(brand);
  return {
    '--canvas-primary': resolvedBrand.primary,
    '--canvas-accent': hexToRgba(resolvedBrand.accent, 0.85),
    '--canvas-primary-foreground': foregroundFor(resolvedBrand.primary),
  };
}

export interface CanvasBrandAttributes {
  'data-canvas-font-style'?: 'serif';
  'data-canvas-corner-style'?: 'square';
}

/**
 * DOM attributes that scope the `canvas-serif:` / `canvas-square:` Tailwind
 * variants (see styles/theme.css). Absent for default brands, so existing
 * canvases render identically.
 */
export function buildCanvasBrandAttributes(
  brand?: CanvasBrandColors,
): CanvasBrandAttributes {
  return {
    ...(brand?.fontStyle === 'SERIF' && {
      'data-canvas-font-style': 'serif' as const,
    }),
    ...(brand?.cornerStyle === 'SQUARE' && {
      'data-canvas-corner-style': 'square' as const,
    }),
  };
}

export function canvasAuthorAdvisor(
  canvas: Pick<CanvasData, 'authorName' | 'authorEmail' | 'authorAvatarUrl'>,
): CanvasFooterAdvisor | null {
  if (!canvas.authorName) {
    return null;
  }
  return {
    name: canvas.authorName,
    email: canvas.authorEmail,
    avatarUrl: canvas.authorAvatarUrl,
  };
}
